import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { User } from '@supabase/supabase-js';
import { userPreferencesManager } from '../lib/userPreferences';
import { favoriteQuotesService } from '../lib/favoriteQuotesService';
import { saveRememberedEmail, clearRememberedEmail, saveRememberMePreference, markPersistentLogin } from '../utils/authStorage';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { googleSignIn } from '../lib/googleSignIn';
import { isAndroidApp } from '../utils/platformDetection';
import { persistAndroidLogin } from '../utils/authSession';

export type AppUser = {
    id: string;
    fullName?: string;
    profilePicture?: string;
    local_currency?: string;
    selected_currencies?: string[];
    default_account_id?: string;
    role?: 'admin' | 'user';
    subscription?: {
        plan: 'free' | 'premium';
        status: 'active' | 'inactive' | 'cancelled' | 'trialing' | 'past_due' | 'expired';
        validUntil: string | null;
        billing_cycle?: 'monthly' | 'yearly' | 'one-time' | 'lifetime';
        next_billing_date?: string | null;
        purchase_details?: Record<string, unknown> | null;
    };
};

interface AuthStore {
    user: User | null;
    profile: AppUser | null;
    isLoading: boolean;
    error: string | null;
    success: string | null;
    setUserAndProfile: (user: User | null, profile: AppUser | null) => void;
    signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ success: boolean; message?: string }>;
    signUp: (email: string, password: string, fullName?: string) => Promise<{ success: boolean; message?: string }>;
    signOut: () => Promise<void>;
    signInWithProvider: (provider: 'google' | 'apple') => Promise<{ success: boolean; message?: string }>;
    fallbackToBrowserOAuth: (provider: 'google' | 'apple', redirectUrl: string) => Promise<{ success: boolean; message?: string }>;
    updateProfile: (updates: Partial<AppUser>) => Promise<{ data: AppUser | null; error: any }>;
    logout: () => Promise<void>;
    deleteAccount: () => Promise<{ success: boolean; error?: string }>;
    clearMessages: () => void;
    handleEmailConfirmation: () => Promise<void>;
    resendEmailConfirmation: (email: string) => Promise<{ success: boolean; message?: string }>;
    resetPassword: (email: string) => Promise<{ success: boolean; message?: string }>;
    checkAuthState: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>()((set, get) => ({
    user: null,
    profile: null,
    isLoading: false,
    error: null,
    success: null,
    setUserAndProfile: async (user, profile) => {
        if (!user) {
            set({ user: null, profile: null, isLoading: false });
            return;
        }
        
        // If profile is provided, always update it (even if one already exists)
        if (profile) {
            set({ user, profile, isLoading: false });
            return;
        }
        
        const currentState = get();
        if (currentState.user?.id === user.id && currentState.profile) {
            return;
        }
        
        if (!profile) {
            set({ user, profile: null, isLoading: true });
            const userId = user.id;
            
            setTimeout(async () => {
                // Abort if user signed out or switched during the delay
                if (get().user?.id !== userId) return;

                try {
                    const { data: existingProfile, error: fetchError } = await supabase
                        .from('profiles')
                        .select('*')
                        .eq('id', userId)
                        .single();

                    if (get().user?.id !== userId) return;
                    
                    if (existingProfile && !fetchError) {
                        const profileData: AppUser = {
                            id: existingProfile.id,
                            fullName: existingProfile.full_name,
                            profilePicture: existingProfile.profile_picture,
                            local_currency: existingProfile.local_currency,
                            selected_currencies: existingProfile.selected_currencies,
                            default_account_id: existingProfile.default_account_id,
                            role: existingProfile.role === 'admin' ? 'admin' : 'user',
                            subscription: existingProfile.subscription
                        };
                        set({ user, profile: profileData, isLoading: false });
                        return;
                    }

                    const newProfile: AppUser = {
                        id: userId,
                        fullName: user.user_metadata?.full_name || user.user_metadata?.fullName || 'User',
                        local_currency: undefined,
                        selected_currencies: undefined,
                        default_account_id: undefined,
                        role: 'user',
                        subscription: { plan: 'free', status: 'active', validUntil: null }
                    };
                    
                    const { error: saveError } = await supabase
                        .from('profiles')
                        .upsert({
                            id: newProfile.id,
                            full_name: newProfile.fullName,
                            local_currency: null,
                            selected_currencies: null,
                            subscription: newProfile.subscription,
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString()
                        }, {
                            onConflict: 'id'
                        });

                    if (get().user?.id !== userId) return;

                    if (!saveError) {
                        set({ user, profile: newProfile, isLoading: false });
                    } else {
                        set({ user, profile: newProfile, isLoading: false });
                    }
                } catch {
                    if (get().user?.id !== userId) return;
                    set({
                        user,
                        profile: {
                            id: userId,
                            fullName: user.user_metadata?.full_name || user.user_metadata?.fullName || 'User',
                            role: 'user',
                            subscription: { plan: 'free', status: 'active', validUntil: null }
                        },
                        isLoading: false
                    });
                }
            }, 100);
            
            return;
        }
        
        set({ user, profile, isLoading: false });
    },
  updateProfile: async (updates) => {
    const { user } = get();
    if (!user) {
      const error = { message: 'User not logged in' };
      throw error;
    }
    try {
      // Build the final payload with snake_case keys that match the database.
      // We only include properties that have a non-undefined value.
      const dbPayload: { [key: string]: any } = { id: user.id };
      if (updates.fullName !== undefined) dbPayload.full_name = updates.fullName;
      if (updates.local_currency !== undefined) dbPayload.local_currency = updates.local_currency;
      if (updates.profilePicture !== undefined) dbPayload.profile_picture = updates.profilePicture;
      if (updates.selected_currencies !== undefined) dbPayload.selected_currencies = updates.selected_currencies;
      if (updates.default_account_id !== undefined) dbPayload.default_account_id = updates.default_account_id;

      const { data, error } = await supabase
        .from('profiles')
        .upsert(dbPayload, {
          onConflict: 'id',
        })
        .select('id, full_name, profile_picture, local_currency, selected_currencies, default_account_id, subscription, role')
        .single();

      if (error) {

        throw error;
      }
      
      // The database returns snake_case columns. We map them back to camelCase
      // for the application's state.
      const profileData: AppUser = {
        id: data.id,
        fullName: data.full_name,
        profilePicture: data.profile_picture,
        local_currency: data.local_currency,
        selected_currencies: data.selected_currencies,
        default_account_id: data.default_account_id,
        role: data.role === 'admin' ? 'admin' : 'user',
        subscription: data.subscription || get().profile?.subscription || { plan: 'free', status: 'active' },
      };
      
      set({ profile: profileData });
      return { data: profileData, error: null };
    } catch (error: any) {
      // Return a structured error to the component.
      return { data: null, error };
    }
  },
      logout: async () => {
      const { user } = get();
      if (user?.id) {
        userPreferencesManager.clearCache(user.id);
        favoriteQuotesService.clearCache(user.id);
      }
      await supabase.auth.signOut();
      set({ user: null, profile: null });
    },
    signOut: async () => {
      const { user } = get();
      if (user?.id) {
        userPreferencesManager.clearCache(user.id);
        favoriteQuotesService.clearCache(user.id);
      }
      await supabase.auth.signOut();
      set({ user: null, profile: null });
    },
  deleteAccount: async () => {
    const { user } = get();
    if (!user) {
      return { success: false, error: 'User not logged in' };
    }

    try {
      const userId = user.id;

      
      // Use the simple disable database function

      const { data, error } = await supabase.rpc('delete_user_simple_disable', {
        user_id: userId
      });
      
      if (error) {

        return { success: false, error: error.message };
      }
      

      if (data === true) {

        
        // Sign out and clear session
        await supabase.auth.signOut();


        // Clear local state
        set({ user: null, profile: null });

        return { success: true };
      } else {

        return { success: false, error: 'Database deletion failed' };
      }
    } catch (error: any) {

      return { success: false, error: error.message || 'Failed to delete account' };
    }
  },

  signIn: async (email: string, password: string, rememberMe: boolean = false) => {
    set({ isLoading: true, error: null, success: null });
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {

        set({ error: error.message, isLoading: false });
        return { success: false, message: error.message };
      }

      // Handle remember me functionality
      if (rememberMe) {
        saveRememberedEmail(email);
      } else {
        clearRememberedEmail();
      }
      if (isAndroidApp()) persistAndroidLogin();
      else saveRememberMePreference(rememberMe);
      
      // Set user immediately to trigger navigation
      set({ user: data.user, isLoading: false });
      
      // Fetch profile in background (don't await it)
      setTimeout(() => {
        const { setUserAndProfile } = get();
        setUserAndProfile(data.user, null);
      }, 100);
      
      return { success: true };
    } catch (error) {

      const errorMessage = (error as Error).message;
      set({ error: errorMessage, isLoading: false });
      return { success: false, message: errorMessage };
    }
  },

  signUp: async (email: string, password: string, fullName?: string) => {
    set({ isLoading: true, error: null, success: null });
    
    try {

      
      // CRITICAL: Always check for existing email - NO FALLBACK for security
      const { data: emailCheck, error: emailCheckError } = await supabase.rpc('check_email_exists', {
        email_to_check: email
      });
      
      // If the email check function fails, BLOCK registration for security
      if (emailCheckError) {

        const errorMessage = 'Unable to verify email availability. Please try again later.';
        set({ error: errorMessage, isLoading: false });
        return { success: false, message: errorMessage };
      }
      
      // If email exists, block registration
      if (emailCheck === true) {
        const userFriendlyError = 'This email is already registered. Please use a different email or try logging in.';
        set({ error: userFriendlyError, isLoading: false });
        return { success: false, message: userFriendlyError };
      }
      

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName
          }
          // Removed emailRedirectTo for auto-confirmation
        }
      });

      if (error) {

        
        const errorMessage = error.message.toLowerCase();
        if (errorMessage.includes('already registered') || 
            errorMessage.includes('already exists') ||
            errorMessage.includes('user already registered') ||
            errorMessage.includes('email already exists') ||
            errorMessage.includes('user already exists')) {
          const userFriendlyError = 'This email is already registered. Please use a different email or try logging in.';
          set({ error: userFriendlyError, isLoading: false });
          return { success: false, message: userFriendlyError };
        }
        
        set({ error: error.message, isLoading: false });
        return { success: false, message: error.message };
      }

      if (!data.user) {

        set({ error: 'Registration failed. Please try again.', isLoading: false });
        return { success: false, message: 'Registration failed. Please try again.' };
      }


      // Auto-confirm user for better UX - no email confirmation required
      // Use setUserAndProfile to fetch the profile (required for WelcomeModal check)
      const { setUserAndProfile } = get();
      await setUserAndProfile(data.user, null);

      set({ 
        success: 'Account created successfully! Welcome to Balanze!',
        error: null,
        isLoading: false
      });

      setTimeout(() => {
        set(state => ({ ...state, success: null }));
      }, 5000);

      return { success: true, message: 'Account created successfully! Welcome to Balanze!' };
      
    } catch (error) {

      const errorMessage = (error as Error).message;
      set({ error: errorMessage, isLoading: false });
      return { success: false, message: errorMessage };
    }
  },

  signInWithProvider: async (provider: 'google' | 'apple') => {
    // CRITICAL: Log platform detection - this will now show in Logcat via WebView console
    const detectedPlatform = Capacitor.getPlatform();
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown';
    const hasCapacitor = typeof (window as any).Capacitor !== 'undefined';
    
    console.error('========================================');
    console.error('========== OAUTH FLOW STARTING ==========');
    console.error('Provider:', provider);
    console.error('Capacitor.getPlatform():', detectedPlatform);
    console.error('User Agent:', userAgent);
    console.error('Has Capacitor:', hasCapacitor);
    console.error('Window location:', window.location.href);
    console.error('========================================');
    
    // Native logging to Android Logcat (if plugin available)
    try {
      const capacitor = (window as any).Capacitor;
      if (capacitor?.Plugins?.GoogleSignIn?.log) {
        await capacitor.Plugins.GoogleSignIn.log({ message: `[OAUTH] Platform: ${detectedPlatform}` });
      }
    } catch (e) {
      // Ignore logging errors
    }
    
    set({ isLoading: true, error: null, success: null });
    try {
      const redirectUrl = `https://balanze.cash/auth/callback`;
      console.error('[OAUTH] 🔍 OAuth Configuration:');
      console.error('[OAUTH] - Redirect URL:', redirectUrl);
      console.error('[OAUTH] - Window origin:', window.location.origin);
      console.error('[OAUTH] - Current URL:', window.location.href);
      
      // Check if we're on Android
      const isAndroid = Capacitor.getPlatform() === 'android';
      
      // Native logging
      try {
        const capacitor = (window as any).Capacitor;
        if (capacitor?.Plugins?.GoogleSignIn?.log) {
          await capacitor.Plugins.GoogleSignIn.log({ message: `[OAUTH] Is Android platform? ${isAndroid}` });
        }
      } catch (e) {
        console.error('[OAUTH] Native logging failed:', e);
      }
      
      console.error('[OAUTH] Is Android platform?', isAndroid);
      
      if (isAndroid && provider === 'google') {
        // Native only — never open Supabase browser OAuth on Android Google login
        try {
          const nativeResult = await googleSignIn.signIn();
          if (!nativeResult?.idToken) {
            throw new Error('Native Google Sign-In unavailable');
          }

          const { data, error } = await supabase.auth.signInWithIdToken({
            provider: 'google',
            token: nativeResult.idToken,
          });
          if (error) throw error;
          if (!data.user) throw new Error('Authentication succeeded but no user data received');

          markPersistentLogin();
          await get().setUserAndProfile(data.user, null);
          set({ isLoading: false });
          return { success: true };
        } catch (nativeError: any) {
          const msg = nativeError?.message || '';
          const cancelled = msg.includes('cancelled') || msg.includes('12500');
          const userMessage = cancelled
            ? 'Sign in was cancelled.'
            : 'Google Sign-In failed. Please try again.';
          console.error('[OAUTH] ❌ Native Google sign-in error:', nativeError);
          set({ error: userMessage, isLoading: false });
          return { success: false, message: userMessage };
        }
      } else if (isAndroid && provider === 'apple') {
        // For Apple on Android, use browser OAuth (Apple Sign-In is iOS only)
        console.error('[OAUTH] 📱 Android detected - Apple Sign-In not available on Android, using browser');
        try {
          return await get().fallbackToBrowserOAuth(provider, redirectUrl);
        } catch (fallbackError: any) {
          console.error('[OAUTH] ❌ Browser OAuth failed:', fallbackError);
          throw fallbackError;
        }
      } else {
        // On web/iOS, use default Supabase OAuth flow
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: redirectUrl
          }
        });
        

        if (error) {
          // Provide user-friendly error messages
          let userMessage = 'Social login failed. Please try again.';
          if (error.message.includes('provider is not enabled')) {
            userMessage = 'Social login is not configured yet. Please use email/password login.';
          } else if (error.message.includes('redirect_uri_mismatch')) {
            userMessage = 'Social login configuration error. Please contact support.';
          }
          set({ error: userMessage, isLoading: false });
          return { success: false, message: userMessage };
        }

        return { success: true };
      }
    } catch (error: any) {
      console.error('[OAUTH] ❌ Outer catch block - unexpected error:', error);
      console.error('[OAUTH] - Error type:', typeof error);
      console.error('[OAUTH] - Error message:', error?.message);
      console.error('[OAUTH] - Error stack:', error?.stack);
      
      const errorMessage = error?.message || 'An unexpected error occurred during social login';
      
      // Provide more specific error messages
      let userMessage = 'Social login failed. Please try again.';
      if (errorMessage.includes('provider is not enabled')) {
        userMessage = 'Social login is not configured yet. Please use email/password login.';
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        userMessage = 'Network error. Please check your internet connection and try again.';
      } else if (errorMessage.includes('cancelled')) {
        userMessage = 'Sign in was cancelled.';
      }
      
      set({ error: userMessage, isLoading: false });
      return { success: false, message: userMessage };
    }
  },

  // Fallback to browser-based OAuth (for Apple on Android or when native sign-in fails)
  fallbackToBrowserOAuth: async (provider: 'google' | 'apple', redirectUrl: string) => {
    try {
      console.error('[OAUTH] 🔄 Using browser-based OAuth fallback...');
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: redirectUrl,
          skipBrowserRedirect: true
        }
      });
      
      if (error) {
        console.error('[OAUTH] ❌ Supabase OAuth error:', error);
        throw error;
      }
      
      if (data?.url) {
        console.error('[OAUTH] 🌐 Opening browser with OAuth URL...');
        try {
          await Browser.open({ url: data.url });
          console.error('[OAUTH] ✅ Browser opened successfully');
          return { success: true };
        } catch (browserError: any) {
          // Do not window.open into the Capacitor WebView (navigates to Supabase URL)
          console.error('[OAUTH] ⚠️ Browser.open() failed:', browserError);
          throw new Error('Unable to open browser. Please ensure you have a browser installed.');
        }
      } else {
        throw new Error('OAuth URL not received');
      }
    } catch (error: any) {
      console.error('[OAUTH] ❌ Browser OAuth fallback error:', error);
      let userMessage = 'Social login failed. Please try again.';
      if (error?.message?.includes('provider is not enabled')) {
        userMessage = 'Social login is not configured yet. Please use email/password login.';
      } else if (error?.message?.includes('redirect_uri_mismatch')) {
        userMessage = 'Social login configuration error. Please contact support.';
      }
      set({ error: userMessage, isLoading: false });
      return { success: false, message: userMessage };
    }
  },

  clearMessages: () => {
    set({ error: null, success: null });
  },

  handleEmailConfirmation: async () => {
    set({ success: 'Email confirmed successfully!' });
    setTimeout(() => {
      set(state => ({ ...state, success: null }));
    }, 3000);
  },

  resendEmailConfirmation: async (email: string) => {
    // Email confirmation is no longer required - auto-confirmation enabled
    return { success: true, message: 'Email confirmation is not required for this account.' };
  },

  resetPassword: async (email: string) => {
    set({ isLoading: true, error: null, success: null });
    try {
      // Use hardcoded URL for Android (like OAuth), dynamic for web
      const isAndroid = Capacitor.getPlatform() === 'android';
      const redirectUrl = isAndroid 
        ? 'https://balanze.cash/auth/reset-password'
        : `${window.location.origin}/auth/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (error) {
        let userMessage = error.message;
        
        // Handle rate limit errors with user-friendly messages
        if (error.message.includes('rate limit') || error.message.includes('429')) {
          userMessage = 'Too many reset attempts. Please wait a few minutes before trying again.';
        } else if (error.message.includes('email not found')) {
          userMessage = 'If an account with this email exists, you will receive a reset link.';
        } else if (error.message.includes('invalid email')) {
          userMessage = 'Please enter a valid email address.';
        }
        
        set({ error: userMessage, isLoading: false });
        return { success: false, message: userMessage };
      }

      set({ 
        isLoading: false, 
        success: 'Password reset email sent! Check your inbox.',
        error: null 
      });

      setTimeout(() => {
        set(state => ({ ...state, success: null }));
      }, 5000);

      return { success: true, message: 'Password reset email sent! Check your inbox.' };
    } catch (error) {
      const errorMessage = (error as Error).message;
      set({ error: errorMessage, isLoading: false });
      return { success: false, message: errorMessage };
    }
  },

  checkAuthState: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {

        return;
      }

      if (session?.user) {

        const { setUserAndProfile } = get();
        await setUserAndProfile(session.user, null);
      }
    } catch (error) {

    }
  }
}));

